// Componente: EditFaltas.jsx
import React, { useState, useMemo } from "react";
import Input from "../../../Elements/Input/Input";
import Button from "../../../Elements/Button/Button";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";

export default function EditFaltas({ values, handleChange }) {
    const faltas = useMemo(() => values.faltas || [], [values.faltas]);

    // — Estado para crear uno nuevo —
    const [newFalta, setNewFalta] = useState({
        fecha: "",
        motivo: "",
        justificada: false,
    });

    // — Estados para el calendario y el modal de edición —
    const [showCal, setShowCal] = useState(false);
    const [editing, setEditing] = useState(null); // { idx, fecha, motivo, justificada }

    // — Eventos para FullCalendar —
    const events = useMemo(
        () =>
            faltas.map((f, i) => ({
                id: String(i),
                title: f.motivo || `Falta ${i + 1}`,
                start: f.fecha,
                allDay: true,
                extendedProps: { ...f, idx: i },
            })),
        [faltas]
    );

    // — Validaciones —
    const canAdd =
        newFalta.fecha !== "" && newFalta.motivo.trim() !== "";
    const canSave =
        editing &&
        editing.fecha !== "" &&
        editing.motivo.trim() !== "";

    // — Agregar una nueva falta —
    const handleAdd = () => {
        if (!canAdd) return;
        const lista = [
            ...faltas,
            { ...newFalta, justificada: !!newFalta.justificada },
        ];
        handleChange("faltas")({ target: { value: lista } });
        setNewFalta({ fecha: "", motivo: "", justificada: false });
    };

    // — Click en evento del calendario para editar —
    const handleEventClick = ({ event }) => {
        const { idx, fecha, motivo, justificada } = event.extendedProps;
        setEditing({ idx, fecha, motivo, justificada });
        setShowCal(false);
    };

    // — Cambios en el modal de edición —
    const handleEditChange = (field) => (e) =>
        setEditing({
            ...editing,
            [field]:
                field === "justificada"
                    ? e.target.value === "true"
                    : e.target.value,
        });


    const saveEdit = () => {
        if (!canSave) return;
        // Clonamos el array para no mutar directamente
        const updated = [...faltas];
        // Reemplazamos en el índice
        updated[editing.idx] = {
            fecha: editing.fecha,
            motivo: editing.motivo,
            justificada: editing.justificada,
        };
        // Actualizamos el formulario de golpe
        handleChange("faltas")({ target: { value: updated } });
        setEditing(null);
    };

    return (
        <div className="seccion-formulario">
            <h4>Faltas</h4>

            {/* — Formulario para crear una falta nueva — */}
            <div className="row g-3 align-items-end">
                <div className="col-md-4">
                    <Input
                        label="Fecha"
                        type="date"
                        value={newFalta.fecha}
                        onChange={(e) =>
                            setNewFalta({ ...newFalta, fecha: e.target.value })
                        }
                    />
                </div>
                <div className="col-md-4">
                    <Input
                        label="Motivo"
                        value={newFalta.motivo}
                        onChange={(e) =>
                            setNewFalta({ ...newFalta, motivo: e.target.value })
                        }
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Justificada</label>
                    <select
                        className="form-select"
                        value={newFalta.justificada ? "true" : "false"}
                        onChange={(e) =>
                            setNewFalta({
                                ...newFalta,
                                justificada: e.target.value === "true",
                            })
                        }
                    >
                        <option value="false">No</option>
                        <option value="true">Sí</option>
                    </select>
                </div>
                <div className="col-md-12">
                    <Button
                        text="Agregar"
                        color="green"
                        fill
                        classNames="btn-sm"
                        action={handleAdd}
                        disabled={!canAdd}
                    />
                </div>
            </div>
            {!canAdd && (
                <small className="text-danger row">
                    Fecha y motivo son obligatorios para crear una falta.
                </small>
            )}

            {/* — Botón para abrir calendario — */}
            <Button
                text="Ver calendario de faltas"
                color="blue"
                fill
                classNames="mt-3"
                action={() => setShowCal(true)}
                disabled={faltas.length === 0}
            />

            {/* — Modal Calendario — */}
            {showCal && (
                <div
                    className="modal d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Calendario de Faltas</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowCal(false)}
                                />
                            </div>
                            <div className="modal-body">
                                <FullCalendar
                                    plugins={[dayGridPlugin, interactionPlugin]}
                                    initialView="dayGridMonth"
                                    locale={esLocale}
                                    headerToolbar={{
                                        left: "prev,next today",
                                        center: "title",
                                        right: "",
                                    }}
                                    events={events}
                                    eventClick={handleEventClick}
                                    height="auto"
                                    dayMaxEventRows={3}
                                    dayMaxEvents
                                    moreLinkContent={(args) => `+${args.num} más`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* — Modal de edición de una falta existente — */}
            {editing && (
                <div
                    className="modal d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog">
                        <div className="modal-content p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Editar Falta</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setEditing(null)}
                                />
                            </div>
                            <Input
                                label="Fecha"
                                type="date"
                                value={editing.fecha}
                                onChange={handleEditChange("fecha")}
                            />
                            <Input
                                label="Motivo"
                                value={editing.motivo}
                                onChange={handleEditChange("motivo")}
                            />
                            <div className="mt-2">
                                <label className="form-label">Justificada</label>
                                <select
                                    className="form-select"
                                    value={editing.justificada ? "true" : "false"}
                                    onChange={handleEditChange("justificada")}
                                >
                                    <option value="false">No</option>
                                    <option value="true">Sí</option>
                                </select>
                            </div>
                            {!canSave && (
                                <small className="text-danger mt-2">
                                    Completa todos los campos para guardar.
                                </small>
                            )}
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button
                                    text="Guardar"
                                    color="green"
                                    fill
                                    action={saveEdit}
                                    disabled={!canSave}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
